// frontend/app/debugDump.js
function normVideos(item) {
  const arr = (item.videos || item.videos_payload || []);
  return arr.map((v, i) => ({
    idx: i,
    name: v.name || v.segment_name || `Segment ${i + 1}`,
    // your handlers prefer source_id (aka video_id)
    video_id: v.source_id ?? v.video_source_id ?? v.video_id ?? v.id ?? null,
    tag_id: v.tag_id ?? v.tag ?? null,
    // sometimes useful to see if you prefer uuid/version instead
    uuid: v.uuid ?? null,
    version: v.version ?? v.video_version ?? null,
    // flags you were logging
    outsideCollection: !!v.outsideCollection,
    source_id: v.source_id ?? null,
    raw: v,
  })).filter(v => v.video_id && (v.tag_id || v.uuid)); // keep useful ones
}

function normPwos(item) {
  const arr = (item.pwos || item.pwos_payload || []);
  return arr.map((p, i) => ({
    idx: i,
    name: p.name || p.scene_title || `Scene ${i + 1}`,
    pwo_source_id: p.pwo_source_id ?? p.source_id ?? p.id ?? null,
    scene_name: p.scene_name ?? p.scene ?? null,
    outsideCollection: !!p.outsideCollection,
    raw: p,
  })).filter(p => p.pwo_source_id && p.scene_name);
}
// function normQuiz(item) {
//   // Prefer server-style shapes, but be liberal in what we accept
//   const arr =
//     item.quiz ||
//     item.quiz_payload ||
//     item.questions ||
//     [];

//   // Normalize a few helpful columns for console.table
//   return arr
//     .map((q, i) => ({
//       idx: i,
//       // Question identifier
//       question_id: q.question_id ?? q.id ?? q.qid ?? null,
//       // Quick glance fields (if present)
//       type: q.type ?? q.question_type ?? q.format ?? null,
//       lang: q.language_id ?? q.lang ?? null,
//       topic_id: q.topic_id ?? q.source_topic_id ?? null,
//       // A short label preview (keeps console.table readable)
//       label:
//         (q.text || q.question_text || q.prompt || q.title || '')
//           .toString()
//           .replace(/\s+/g, ' ')
//           .slice(0, 80),
//       raw: q,
//     }))
//     .filter(q => q.question_id);
// }
// --- robust quiz normalizer: handles arrays, objects, and {question_ids: []}
function normQuiz(item) {
  function coerceQuizArray(input) {
    if (!input) return [];
    // already an array of questions (or ids)
    if (Array.isArray(input)) return input;

    // object shapes we’ve seen in the wild
    if (typeof input === "object") {
      if (Array.isArray(input.quiz)) return input.quiz;
      if (Array.isArray(input.questions)) return input.questions;
      if (Array.isArray(input.items)) return input.items;
      if (Array.isArray(input.question_ids)) {
        // convert bare ids → {question_id: id}
        return input.question_ids.map((id) => ({ question_id: id }));
      }
      // single question object?
      if (input.question_id || input.id || input.qid) return [input];
    }
    // unknown shape → nothing
    return [];
  }

  // Try all possible carriers on the item, first non-empty wins.
  const raw =
    coerceQuizArray(item?.quiz) ||
    coerceQuizArray(item?.quiz_payload) ||
    coerceQuizArray(item?.questions) ||
    [];

  // Normalize each question for console.table visibility
  return raw
    .map((q, i) => {
      // If backend gave us bare ids (number/string)
      const primitiveId =
        (typeof q === "number" || typeof q === "string") ? String(q) : null;

      const question_id =
        primitiveId ??
        q?.question_id ??
        q?.id ??
        q?.qid ??
        null;

      const labelSource =
        primitiveId ? "" :
        (q?.text ?? q?.question_text ?? q?.prompt ?? q?.title ?? "");

      return {
        idx: i,
        question_id,
        type: primitiveId ? null : (q?.type ?? q?.question_type ?? q?.format ?? null),
        lang: primitiveId ? null : (q?.language_id ?? q?.lang ?? null),
        topic_id: primitiveId ? null : (q?.topic_id ?? q?.source_topic_id ?? null),
        label: labelSource.toString().replace(/\s+/g, " ").slice(0, 80),
        raw: q,
      };
    })
    .filter((q) => !!q.question_id);
}

function oneTopicSkeleton(topicName, source_topic_id, pwos, videos, quizQuestions) {
  const firstPwo = pwos[0] || {};
  const firstVid = videos[0] || {};
  return {
    source_topic_id,
    gold_prompts: [
      // fill your exact gold prompts here
    ],
    pwo: firstPwo.pwo_source_id ? {
      source_pwo_id: firstPwo.pwo_source_id,
      scene_name: firstPwo.scene_name
    } : undefined,
    video: firstVid.video_id ? {
      video_id: firstVid.video_id,
      tag_id: firstVid.tag_id || undefined
    } : (firstVid.uuid ? { uuid: firstVid.uuid, version: firstVid.version } : undefined),
    quiz: Array.isArray(quizQuestions) && quizQuestions.length ? { questions: quizQuestions } : undefined,
    __topic_title: topicName // just for your eyes while pasting
  };
}

export function dumpActiveCollectionForPreset(collectionResolved, collectionMeta) {
  try {
    if (!collectionResolved?.items?.length) {
      console.warn("No items in collectionResolved. Open a collection first.");
      return;
    }

    const collId = collectionMeta?.id ?? "(unknown)";
    const collName = collectionMeta?.name ?? "(untitled)";
    console.group(`📚 Collection ${collName} [id=${collId}] — Demo preset candidates`);

    const topicsOut = [];

    collectionResolved.items.forEach((it, idx) => {
      const topicTitle = it.title || it.topic || `Topic ${idx + 1}`;
      const source_topic_id = it.source_topic_id
        ?? it.topic_snapshot?.source_topic_id
        ?? it.board_topic_id
        ?? it.topic_snapshot?.board_topic_id
        ?? null;

      const videos = normVideos(it);
      const pwos   = normPwos(it);

      // Prefer quiz attached to the item; fallback to global cache of full questions
      let quizFull = Array.isArray(it.quiz?.questions) ? it.quiz.questions
                    : Array.isArray(it.quiz) ? it.quiz
                    : (typeof window !== "undefined" && Array.isArray(window.__latestQuizFull) ? window.__latestQuizFull : []);
      if (quizFull.length) {
        console.log("📝 Quiz (full) candidates:");
        console.table(quizFull.slice(0, 5).map(q => ({
          question_id: q?.question_id,
          type: q?.question_type ?? q?.type ?? null,
          label: (q?.question?.text || q?.question_text || "").toString().replace(/\s+/g, " ").slice(0, 80),
        })));
      }


      console.groupCollapsed(`📌 Topic: ${topicTitle}  (source_topic_id=${source_topic_id})`);
      if (pwos.length) {
        console.log("🔧 PWO candidates:");
        console.table(pwos.map(p => ({
          idx: p.idx,
          name: p.name,
          pwo_source_id: p.pwo_source_id,
          scene_name: p.scene_name,
          outsideCollection: p.outsideCollection
        })));
      } else {
        console.log("🔧 PWO candidates: none found");
      }

      if (videos.length) {
        console.log("🎞️ Video candidates:");
        console.table(videos.map(v => ({
          idx: v.idx,
          name: v.name,
          video_id: v.video_id,
          tag_id: v.tag_id,
          uuid: v.uuid,
          version: v.version,
          outsideCollection: v.outsideCollection
        })));
      } else {
        console.log("🎞️ Video candidates: none found");
      }

      // pick up to 3 question_ids for the skeleton (your demo preset)
      const quizPick = quizFull.slice(0, 3);            // pick 3 to keep demo-preset compact
      const skeleton = oneTopicSkeleton(topicTitle, source_topic_id, pwos, videos, quizPick);

      topicsOut.push(skeleton);

      console.log("📋 Suggested demo_preset.json snippet for THIS topic:");
      console.log(JSON.stringify(skeleton, null, 2));

      if (navigator?.clipboard?.writeText) {
        navigator.clipboard
          .writeText(JSON.stringify(skeleton, null, 2))
          .then(() => console.log("✅ Copied this topic’s snippet to clipboard"))
          .catch(() => {});
      }

      console.groupEnd();
    });

    const presetBlock = {
      DEMO_PRESET: "investor_2025_10_14",
      topics: topicsOut,
      flags: {
        FORCE_AUTOWALKTHROUGH: true,
        SHOW_METRICS_OVERLAY: true,
        SHORT_ANSWER_MODE: true
      }
    };

    console.log("🧩 Combined preset block (you can paste into demo_preset.json and then edit):");
    console.log(JSON.stringify(presetBlock, null, 2));
    console.groupEnd();

    return presetBlock;
  } catch (e) {
    console.error("dumpActiveCollectionForPreset failed:", e);
  }
}
